import { useSaveOrderTemplate } from '@my/tms-api/hooks';
import { TextInput, cn, showToast } from '@my/ui';
import { BookmarkPlus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { TextAreaInput } from './inputs';

/**
 * Name (and describe) a template — for "Save as template" and for Rename on
 * the Templates list. A small popup on the same shell as History and Copy.
 */
export function TemplateNameDialog({
  open,
  title,
  subtitle,
  initialName,
  initialDescription,
  confirmLabel,
  busy,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  initialName: string;
  initialDescription: string;
  confirmLabel: string;
  busy: boolean;
  onConfirm: (name: string, description: string | null) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);

  // Fresh values each time it opens.
  useEffect(() => {
    if (!open) return;
    setName(initialName);
    setDescription(initialDescription);
  }, [open, initialName, initialDescription]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;
  const canSave = name.trim().length > 0 && !busy;

  return createPortal(
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 p-4 pt-24"
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="template-name-dialog-title"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSave) onConfirm(name.trim(), description.trim() || null);
        }}
        className="w-full max-w-md overflow-hidden rounded-md bg-white shadow-xl"
      >
        <header className="flex items-start justify-between gap-2 border-b border-slate-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <BookmarkPlus className="h-4 w-4 text-slate-400" />
            <div>
              <h3 id="template-name-dialog-title" className="text-sm font-semibold text-slate-900">
                {title}
              </h3>
              {subtitle && <p className="text-[11px] text-slate-500">{subtitle}</p>}
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="space-y-3 p-4">
          <TextInput
            label="Template name *"
            value={name}
            maxLength={128}
            autoFocus
            onChange={(e) => setName(e.target.value)}
          />
          <TextAreaInput
            label="Description"
            value={description}
            maxLength={512}
            rows={2}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <footer className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Saving…' : confirmLabel}
          </button>
        </footer>
      </form>
    </div>,
    document.body,
  );
}

/**
 * "Save as template": the order, as last saved, becomes one of its customer's
 * templates under a descriptive name. Used from Order Details (Actions) and
 * the Orders list's row menu.
 */
export function SaveAsTemplateDialog({
  orderId,
  orderNumber,
  suggestedName,
  open,
  onClose,
}: {
  orderId: string;
  orderNumber: string;
  /** e.g. "Carbery Group → Dublin Castle · Full Load". */
  suggestedName: string;
  open: boolean;
  onClose: () => void;
}) {
  const save = useSaveOrderTemplate();
  return (
    <TemplateNameDialog
      open={open}
      title={`Save ${orderNumber} as a template`}
      subtitle="The order as last saved, without its dates — for this customer's New orders."
      initialName={suggestedName}
      initialDescription=""
      confirmLabel="Save template"
      busy={save.isPending}
      onClose={onClose}
      onConfirm={(name, description) =>
        save.mutate(
          { sourceOrderId: orderId, name, description },
          {
            onSuccess: (t) => {
              onClose();
              showToast({
                type: 'success',
                message: `Template “${t.name}” saved.`,
                durationMs: 6000,
              });
            },
            onError: (err) =>
              showToast({
                type: 'error',
                message: err instanceof Error ? err.message : 'Couldn’t save the template.',
              }),
          },
        )
      }
    />
  );
}

/** Order Details → Actions: "Save as template", next to Copy. */
export function SaveAsTemplateButton({
  orderId,
  orderNumber,
  suggestedName,
}: {
  orderId: string | null;
  orderNumber: string | null;
  suggestedName: string;
}) {
  const [open, setOpen] = useState(false);
  if (!orderId || !orderNumber) return null;
  return (
    <>
      <button
        type="button"
        aria-label="Save as template"
        title="Save this order as a template for this customer"
        onClick={() => setOpen(true)}
        className={cn(
          // Same 28px square as History and Copy beside it.
          'inline-flex h-7 w-7 items-center justify-center rounded-md',
          'border border-slate-300 bg-white text-slate-500',
          'hover:bg-slate-50 hover:text-slate-700',
        )}
      >
        <BookmarkPlus className="h-3.5 w-3.5" />
      </button>
      <SaveAsTemplateDialog
        orderId={orderId}
        orderNumber={orderNumber}
        suggestedName={suggestedName}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
